import React, { useEffect, useMemo } from 'react';
import { StockStatItem, WarehouseItem, StockAlert } from '../stockManagementTypes';
import { useSkusStore } from '../../../../store/skusStore';
import { useStockTransfersStore } from '../../../../store/stockTransfersStore';
import styles from './DashboardTab.module.css';

const statIcons = [
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/></svg>,
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/></svg>,
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>,
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1z"/></svg>,
];

const statIcons2 = statIcons;

const alertSeverityColors: Record<string, string> = {
  critical: '#DC2626',
  warning: '#D97706',
  expiring: '#D97706',
  info: '#6B7280',
};

const StatCard: React.FC<{ stat: StockStatItem; icon: React.ReactNode }> = ({ stat, icon }) => (
  <div className={styles.statCard}>
    <div className={styles.statTop}>
      <div className={styles.statIcon} style={{ background: stat.iconBg }}>
        <span style={{ color: stat.iconColor }}>{icon}</span>
      </div>
      <span className={styles.statBadge} style={{ background: stat.badgeColor, color: 'white' }}>{stat.badge}</span>
    </div>
    <p className={styles.statValue} style={{ color: stat.iconColor }}>{stat.value}</p>
    <p className={styles.statLabel}>{stat.label}</p>
  </div>
);

export const DashboardTab: React.FC = () => {
  const skus = useSkusStore((s) => s.items);
  const fetchSkus = useSkusStore((s) => s.fetchList);
  const transfers = useStockTransfersStore((s) => s.items);
  const fetchTransfers = useStockTransfersStore((s) => s.fetchList);

  useEffect(() => {
    fetchSkus();
    fetchTransfers();
  }, [fetchSkus, fetchTransfers]);

  const totalUnits = useMemo(() => skus.reduce((sum, s) => sum + s.total_stock, 0), [skus]);
  const inventoryValue = useMemo(() => skus.reduce((sum, s) => sum + s.total_stock * s.unit_cost, 0), [skus]);
  const lowStock = useMemo(() => skus.filter((s) => s.total_stock < 50).length, [skus]);
  const inTransit = useMemo(() => transfers.filter((t) => t.status === 'in_transit').length, [transfers]);

  const stockStatsRow1: StockStatItem[] = [
    { label: 'Total SKUs', value: String(skus.length), badge: 'Active', badgeColor: '#059669', iconBg: '#ECFDF5', iconColor: '#059669' },
    { label: 'Total Units', value: totalUnits.toLocaleString(), badge: `$${Math.round(inventoryValue / 1000)}K inventory`, badgeColor: '#2563EB', iconBg: '#EFF6FF', iconColor: '#2563EB' },
    { label: 'Low Stock Alerts', value: String(lowStock), badge: lowStock > 0 ? 'Needs attention' : 'All healthy', badgeColor: lowStock > 0 ? '#DC2626' : '#059669', iconBg: '#FEF2F2', iconColor: '#DC2626' },
    { label: 'In Transit', value: String(inTransit), badge: 'Active transfers', badgeColor: '#D97706', iconBg: '#FFFBEB', iconColor: '#D97706' },
  ];

  const stockStatsRow2: StockStatItem[] = [
    { label: 'Reserved Stock', value: '—', badge: 'Coming soon', badgeColor: '#6B7280', iconBg: '#F3F4F6', iconColor: '#6B7280' },
    { label: 'Pending Receiving', value: '—', badge: 'Coming soon', badgeColor: '#6B7280', iconBg: '#F3F4F6', iconColor: '#6B7280' },
    { label: 'On Hold', value: '—', badge: 'Coming soon', badgeColor: '#6B7280', iconBg: '#F3F4F6', iconColor: '#6B7280' },
    { label: 'Turnover Rate', value: '—', badge: 'Coming soon', badgeColor: '#6B7280', iconBg: '#F3F4F6', iconColor: '#6B7280' },
  ];

  const warehouses: WarehouseItem[] = [];
  const stockAlerts: StockAlert[] = [];

  return (
    <div>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Stock Overview</h2>
          <p className={styles.subtitle}>Inventory monitoring — detect, investigate & correct in real time</p>
        </div>
        <button
          type="button"
          className={styles.fullInvBtn}
          disabled
          title="Full inventory sync endpoint not yet available"
        >
          Full Inventory
        </button>
      </div>

      <div className={styles.statsGrid}>
        {stockStatsRow1.map((stat, i) => (
          <StatCard key={stat.label} stat={stat} icon={statIcons[i]} />
        ))}
      </div>

      <div className={styles.statsGrid}>
        {stockStatsRow2.map((stat, i) => (
          <StatCard key={stat.label} stat={stat} icon={statIcons2[i]} />
        ))}
      </div>

      {warehouses.length > 0 && (
        <>
          <h3 className={styles.sectionTitle}>Warehouse Summary</h3>
          <div className={styles.warehouseGrid}>
            {warehouses.map((wh) => (
              <div key={wh.name} className={styles.warehouseCard}>
                <div className={styles.warehouseBar} style={{ background: wh.barColor }} />
                <div className={styles.warehouseBody}>
                  <h4 className={styles.warehouseName}>{wh.name}</h4>
                  <p className={styles.warehouseType}>{wh.type}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className={styles.alertsCard}>
        <div className={styles.alertsHeader}>
          <svg className={styles.alertsIcon} viewBox="0 0 24 24" fill="currentColor"><path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/></svg>
          <h3 className={styles.alertsTitle}>Stock alerts</h3>
        </div>
        <div className={styles.alertsList}>
          {stockAlerts.length === 0 ? (
            <p style={{ padding: 16, color: '#6B7280', fontSize: 14 }}>No active alerts.</p>
          ) : stockAlerts.map((alert) => (
            <div key={alert.id} className={styles.alertItem}>
              <span className={styles.alertDot} style={{ background: alertSeverityColors[alert.severity] }} />
              <div className={styles.alertContent}>
                <p className={styles.alertMessage}>{alert.message}</p>
                <p className={styles.alertMeta}>{alert.location} &middot; {alert.time}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
